import { useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import { api, getToken, setToken } from "@/lib/insightsApi";

const initialArticle = () => ({
  title: { en: "", pt: "", el: "" },
  slug: "",
  excerpt: { en: "", pt: "", el: "" },
  content: { en: "", pt: "", el: "" },
  featured_image: "",
  category: "",
  author_name: "",
  author_role: "",
  seo_title: { en: "", pt: "", el: "" },
  meta_description: { en: "", pt: "", el: "" },
  related_programme: "",
  related_article_slug: "",
  cta_label: "",
  cta_href: "",
  cta_target_asset_id: "",
  cta_target_service: "",
  cta_placement: "article_bottom",
  status: "draft",
  published_at: null,
});

const initialCase = () => ({
  title: { en: "", pt: "", el: "" },
  slug: "",
  client_name: "",
  anonymous: false,
  collaboration_partner: "",
  challenge: { en: "", pt: "", el: "" },
  approach: { en: "", pt: "", el: "" },
  outcomes: { en: "", pt: "", el: "" },
  metrics: { en: "", pt: "", el: "" },
  testimonial: { en: "", pt: "", el: "" },
  testimonial_author: "",
  featured_image: "",
  related_programme: "",
  cta_label: "",
  cta_href: "",
  cta_target_asset_id: "",
  cta_target_service: "",
  cta_placement: "case_study_bottom",
  status: "draft",
  published_at: null,
});

const PROGRAMMES = [
  { value: "", label: "— none —" },
  { value: "high-stakes-presentation-coaching", label: "High-Stakes Presentation Coaching" },
  { value: "executive-presence-development",    label: "Executive Presence Development" },
  { value: "presentation-excellence",           label: "Presentation & Communication Excellence" },
  { value: "executive-presence-for-leaders",    label: "Executive Presence for Leaders" },
  { value: "custom-programs",                   label: "Bespoke Corporate Programmes" },
];

// -----------------------------------------------------------------------------
// Inputs
// -----------------------------------------------------------------------------
const Field = ({ label, hint, children }) => (
  <label className="flex flex-col gap-1.5">
    <span className="font-body text-xs uppercase tracking-[0.14em] text-ink-soft">{label}</span>
    {children}
    {hint && <span className="font-body text-xs text-ink-soft/70">{hint}</span>}
  </label>
);

const TextInput = ({ value, onChange, ...rest }) => (
  <input
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    className="bg-paper border border-ink/15 px-3 py-2 font-body text-ink focus:outline-none focus:border-terracotta transition-colors"
    {...rest}
  />
);

const TextArea = ({ value, onChange, rows = 4, ...rest }) => (
  <textarea
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    rows={rows}
    className="bg-paper border border-ink/15 px-3 py-2 font-body text-ink focus:outline-none focus:border-terracotta transition-colors resize-y"
    {...rest}
  />
);

const LocalizedField = ({ label, obj, onPatch, area = false, rows = 3 }) => (
  <Field label={label} hint="Only EN is required. PT/EL are optional (Phase 1).">
    <div className="grid gap-2">
      {["en", "pt", "el"].map((loc) => (
        <div key={loc} className="flex items-start gap-2">
          <span className="mt-2 font-hand text-terracotta text-sm w-8 shrink-0 uppercase">{loc}</span>
          {area ? (
            <TextArea value={obj[loc]} onChange={(v) => onPatch({ ...obj, [loc]: v })} rows={rows} />
          ) : (
            <TextInput value={obj[loc]} onChange={(v) => onPatch({ ...obj, [loc]: v })} />
          )}
        </div>
      ))}
    </div>
  </Field>
);

// -----------------------------------------------------------------------------
// Article editor
// -----------------------------------------------------------------------------
const ArticleEditor = ({ initial, onSave, onCancel, onDelete, allArticles = [] }) => {
  const [doc, setDoc] = useState(initial || initialArticle());
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const patch = (k, v) => setDoc((d) => ({ ...d, [k]: v }));

  const submit = async (statusOverride) => {
    setBusy(true); setErr("");
    try {
      const payload = { ...doc };
      if (statusOverride) payload.status = statusOverride;
      const saved = await onSave(payload);
      if (saved?.id) setDoc(saved);
    } catch (e) {
      setErr(e.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-8" data-testid="article-editor">
      <div className="space-y-5">
        <LocalizedField label="Title" obj={doc.title} onPatch={(v) => patch("title", v)} />
        <Field label="Slug" hint="Auto-generated from title if left blank.">
          <TextInput value={doc.slug} onChange={(v) => patch("slug", v)} placeholder="my-article-slug" data-testid="article-slug" />
        </Field>
        <LocalizedField label="Excerpt" obj={doc.excerpt} onPatch={(v) => patch("excerpt", v)} area rows={2} />
        <LocalizedField label="Content (Markdown)" obj={doc.content} onPatch={(v) => patch("content", v)} area rows={12} />
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Featured image URL">
            <TextInput value={doc.featured_image} onChange={(v) => patch("featured_image", v)} placeholder="/images/hero.jpg" />
          </Field>
          <Field label="Category">
            <TextInput value={doc.category} onChange={(v) => patch("category", v)} placeholder="Communication" />
          </Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Author name" hint="Shown near the headline. Leave blank to hide the byline.">
            <TextInput value={doc.author_name} onChange={(v) => patch("author_name", v)} placeholder="e.g. Christos Dionysopoulos" data-testid="article-author-name" />
          </Field>
          <Field label="Author role">
            <TextInput value={doc.author_role} onChange={(v) => patch("author_role", v)} placeholder="e.g. Public Speaking & Executive Presence Coach" data-testid="article-author-role" />
          </Field>
        </div>
        <LocalizedField label="SEO title" obj={doc.seo_title} onPatch={(v) => patch("seo_title", v)} />
        <LocalizedField label="Meta description" obj={doc.meta_description} onPatch={(v) => patch("meta_description", v)} area rows={2} />
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Related programme">
            <select
              value={doc.related_programme}
              onChange={(e) => patch("related_programme", e.target.value)}
              className="bg-paper border border-ink/15 px-3 py-2 font-body text-ink"
            >
              {PROGRAMMES.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </Field>
          <Field label="Publish date (ISO, optional)">
            <TextInput value={doc.published_at || ""} onChange={(v) => patch("published_at", v || null)} placeholder="2026-08-01T09:00:00Z" />
          </Field>
        </div>
        <Field label="Related article" hint="Shown as a 'Related reading' link near the end of the article. Leave as 'None' to hide it.">
          <select
            value={doc.related_article_slug}
            onChange={(e) => patch("related_article_slug", e.target.value)}
            className="bg-paper border border-ink/15 px-3 py-2 font-body text-ink w-full"
            data-testid="article-related-article"
          >
            <option value="">None</option>
            {allArticles
              .filter((a) => a.slug !== doc.slug)
              .map((a) => (
                <option key={a.slug} value={a.slug}>{a.title?.en || a.slug}</option>
              ))}
          </select>
        </Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="CTA label">
            <TextInput value={doc.cta_label} onChange={(v) => patch("cta_label", v)} placeholder="Discuss your objective" />
          </Field>
          <Field label="CTA href">
            <TextInput value={doc.cta_href} onChange={(v) => patch("cta_href", v)} placeholder="/book/presentation" />
          </Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="CTA target asset ID (optional, preferred)">
            <TextInput value={doc.cta_target_asset_id} onChange={(v) => patch("cta_target_asset_id", v)} placeholder="booking_presentation" />
          </Field>
          <Field label="CTA target service (optional, if not an asset)">
            <TextInput value={doc.cta_target_service} onChange={(v) => patch("cta_target_service", v)} placeholder="general_booking" />
          </Field>
        </div>
        <Field label="CTA placement">
          <TextInput value={doc.cta_placement} onChange={(v) => patch("cta_placement", v)} placeholder="article_bottom" />
        </Field>
        <p className="text-xs text-neutral-500 -mt-2">
          Set ONE of the two target fields to the destination's own asset_id (e.g. a booking_* id, contact_request,
          next_presentation_readiness — see backend/asset_registry.json) if the CTA href leads there, and keep CTA
          placement set (the article template only renders this CTA at the bottom of the article today, so leave it
          as "article_bottom" unless that changes). Leave the target fields blank only if the href is a plain
          navigational link (another article, case study, or info page). Publishing with a booking/contact/diagnostic/
          guide/programme/external href and no explicit target + placement is REJECTED (422) — route-fallback
          resolution is only accepted for already-backfilled history, never for new or edited content.
        </p>
      </div>

      <div className="space-y-5">
        <div className="sticky top-24">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <button type="button" onClick={() => setPreview((p) => !p)}
              className="border border-ink/20 px-4 py-2 font-body text-xs uppercase tracking-[0.14em] text-ink hover:border-terracotta hover:text-terracotta transition-colors"
              data-testid="toggle-preview">
              {preview ? "Edit view" : "Preview"}
            </button>
            <span className={`font-body text-xs uppercase tracking-[0.14em] px-2.5 py-1 border ${doc.status === "published" ? "bg-mustard/20 border-mustard text-ink" : "bg-paper border-ink/20 text-ink-soft"}`}>
              {doc.status}
            </span>
          </div>
          {err && <p className="mb-3 font-body text-sm text-terracotta" data-testid="editor-error">{err}</p>}
          <div className="flex flex-wrap gap-3 mb-6">
            <button type="button" disabled={busy} onClick={() => submit("draft")} data-testid="save-draft"
              className="border border-ink/20 px-5 py-3 font-body text-xs uppercase tracking-[0.14em] hover:bg-paper-deep transition-colors">
              Save as draft
            </button>
            <button type="button" disabled={busy} onClick={() => submit("published")} data-testid="save-publish"
              className="bg-ink text-paper px-5 py-3 font-body text-xs uppercase tracking-[0.14em] hover:bg-terracotta transition-colors">
              {doc.id ? "Update & publish" : "Publish"}
            </button>
            {doc.id && doc.status === "published" && (
              <button type="button" disabled={busy} onClick={() => submit("draft")} data-testid="unpublish"
                className="border border-ink/20 px-5 py-3 font-body text-xs uppercase tracking-[0.14em] hover:bg-paper-deep transition-colors">
                Unpublish
              </button>
            )}
            {onCancel && (
              <button type="button" onClick={onCancel} className="font-body text-xs uppercase tracking-[0.14em] text-ink-soft hover:text-ink px-2 py-2">
                Cancel
              </button>
            )}
            {doc.id && onDelete && (
              <button type="button" disabled={busy} onClick={() => onDelete(doc.id)} data-testid="delete-item"
                className="ml-auto text-terracotta font-body text-xs uppercase tracking-[0.14em] hover:underline">
                Delete
              </button>
            )}
          </div>

          {preview ? (
            <div className="bg-paper border border-ink/12 p-6 shadow-[4px_8px_24px_rgba(27,27,24,0.06)]">
              {doc.featured_image && (
                <img src={doc.featured_image} alt="" className="w-full h-auto mb-6 border border-ink/10" />
              )}
              <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-4">{doc.category || "Article"}</p>
              <h1 className="font-serif text-3xl md:text-4xl leading-tight text-ink">{doc.title.en || "Untitled"}</h1>
              {doc.excerpt.en && <p className="mt-4 font-serif italic text-xl text-ink-soft leading-snug">{doc.excerpt.en}</p>}
              {doc.author_name && (
                <p className="mt-4 font-body text-sm text-ink-soft" data-testid="article-author-preview">
                  <span className="text-ink font-medium">{doc.author_name}</span>
                  {doc.author_role && <span> · {doc.author_role}</span>}
                </p>
              )}
              <div className="prose prose-notebook mt-8 max-w-none font-body text-ink leading-relaxed">
                <ReactMarkdown>{doc.content.en || "_No content yet._"}</ReactMarkdown>
              </div>
            </div>
          ) : (
            <div className="bg-paper-deep/40 border border-ink/10 p-6 text-sm font-body text-ink-soft leading-relaxed">
              <p className="font-serif italic text-ink text-base mb-3">Quick tips</p>
              <ul className="list-disc pl-4 space-y-1">
                <li>Write EN first — you can revisit PT and EL later.</li>
                <li>Content supports Markdown: <code>#</code> headings, <code>**bold**</code>, lists, blockquotes.</li>
                <li>Slug auto-generates from title if empty. Keep it short and kebab-case.</li>
                <li>Save as draft any time. Publish when ready.</li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------
// Case study editor
// -----------------------------------------------------------------------------
const CaseEditor = ({ initial, onSave, onCancel, onDelete }) => {
  const [doc, setDoc] = useState(initial || initialCase());
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const patch = (k, v) => setDoc((d) => ({ ...d, [k]: v }));

  const submit = async (statusOverride) => {
    setBusy(true); setErr("");
    try {
      const payload = { ...doc };
      if (statusOverride) payload.status = statusOverride;
      const saved = await onSave(payload);
      if (saved?.id) setDoc(saved);
    } catch (e) {
      setErr(e.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-8" data-testid="case-editor">
      <div className="space-y-5">
        <LocalizedField label="Title" obj={doc.title} onPatch={(v) => patch("title", v)} />
        <Field label="Slug" hint="Auto-generated from title if empty.">
          <TextInput value={doc.slug} onChange={(v) => patch("slug", v)} data-testid="case-slug" />
        </Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Client name">
            <TextInput value={doc.client_name} onChange={(v) => patch("client_name", v)} placeholder="e.g. Acme Corp" />
          </Field>
          <Field label="Anonymous?">
            <label className="inline-flex items-center gap-2 mt-2">
              <input type="checkbox" checked={doc.anonymous} onChange={(e) => patch("anonymous", e.target.checked)} data-testid="case-anonymous" />
              <span className="font-body text-sm">Do not show the client name publicly</span>
            </label>
          </Field>
        </div>
        <Field label="Collaboration partner">
          <TextInput value={doc.collaboration_partner} onChange={(v) => patch("collaboration_partner", v)} placeholder="e.g. Avocado Learning Experiences" />
        </Field>
        <LocalizedField label="Challenge" obj={doc.challenge} onPatch={(v) => patch("challenge", v)} area rows={3} />
        <LocalizedField label="Approach" obj={doc.approach} onPatch={(v) => patch("approach", v)} area rows={4} />
        <LocalizedField label="Outcomes (Markdown supported)" obj={doc.outcomes} onPatch={(v) => patch("outcomes", v)} area rows={4} />
        <LocalizedField label="Metrics" obj={doc.metrics} onPatch={(v) => patch("metrics", v)} area rows={2} />
        <LocalizedField label="Testimonial quote" obj={doc.testimonial} onPatch={(v) => patch("testimonial", v)} area rows={3} />
        <Field label="Testimonial author">
          <TextInput value={doc.testimonial_author} onChange={(v) => patch("testimonial_author", v)} placeholder="Role · Team" />
        </Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Featured image URL">
            <TextInput value={doc.featured_image} onChange={(v) => patch("featured_image", v)} />
          </Field>
          <Field label="Related programme">
            <select
              value={doc.related_programme}
              onChange={(e) => patch("related_programme", e.target.value)}
              className="bg-paper border border-ink/15 px-3 py-2 font-body text-ink"
            >
              {PROGRAMMES.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="CTA label">
            <TextInput value={doc.cta_label} onChange={(v) => patch("cta_label", v)} placeholder="Discuss your objective" />
          </Field>
          <Field label="CTA href">
            <TextInput value={doc.cta_href} onChange={(v) => patch("cta_href", v)} placeholder="/book/presentation" />
          </Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="CTA target asset ID (optional, preferred)">
            <TextInput value={doc.cta_target_asset_id} onChange={(v) => patch("cta_target_asset_id", v)} placeholder="booking_presentation" />
          </Field>
          <Field label="CTA target service (optional, if not an asset)">
            <TextInput value={doc.cta_target_service} onChange={(v) => patch("cta_target_service", v)} placeholder="general_booking" />
          </Field>
        </div>
        <Field label="CTA placement">
          <TextInput value={doc.cta_placement} onChange={(v) => patch("cta_placement", v)} placeholder="case_study_bottom" />
        </Field>
        <p className="text-xs text-neutral-500 -mt-2">
          Set ONE of the two target fields and keep CTA placement set (the case-study template only renders this CTA
          at the bottom of the case study today, so leave it as "case_study_bottom" unless that changes). Leave the
          target fields blank only if the href is a plain navigational link. Publishing with a booking/contact/
          diagnostic/guide/programme/external href and no explicit target + placement is REJECTED (422).
        </p>
      </div>

      <div className="space-y-5">
        <div className="sticky top-24">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <button type="button" onClick={() => setPreview((p) => !p)}
              className="border border-ink/20 px-4 py-2 font-body text-xs uppercase tracking-[0.14em] text-ink hover:border-terracotta hover:text-terracotta transition-colors">
              {preview ? "Edit view" : "Preview"}
            </button>
            <span className={`font-body text-xs uppercase tracking-[0.14em] px-2.5 py-1 border ${doc.status === "published" ? "bg-mustard/20 border-mustard text-ink" : "bg-paper border-ink/20 text-ink-soft"}`}>
              {doc.status}
            </span>
          </div>
          {err && <p className="mb-3 font-body text-sm text-terracotta">{err}</p>}
          <div className="flex flex-wrap gap-3 mb-6">
            <button type="button" disabled={busy} onClick={() => submit("draft")}
              className="border border-ink/20 px-5 py-3 font-body text-xs uppercase tracking-[0.14em] hover:bg-paper-deep transition-colors">
              Save as draft
            </button>
            <button type="button" disabled={busy} onClick={() => submit("published")}
              className="bg-ink text-paper px-5 py-3 font-body text-xs uppercase tracking-[0.14em] hover:bg-terracotta transition-colors">
              {doc.id ? "Update & publish" : "Publish"}
            </button>
            {doc.id && doc.status === "published" && (
              <button type="button" disabled={busy} onClick={() => submit("draft")}
                className="border border-ink/20 px-5 py-3 font-body text-xs uppercase tracking-[0.14em] hover:bg-paper-deep transition-colors">
                Unpublish
              </button>
            )}
            {onCancel && (
              <button type="button" onClick={onCancel} className="font-body text-xs uppercase tracking-[0.14em] text-ink-soft hover:text-ink px-2 py-2">
                Cancel
              </button>
            )}
            {doc.id && onDelete && (
              <button type="button" disabled={busy} onClick={() => onDelete(doc.id)}
                className="ml-auto text-terracotta font-body text-xs uppercase tracking-[0.14em] hover:underline">
                Delete
              </button>
            )}
          </div>

          {preview && (
            <div className="bg-paper border border-ink/12 p-6 shadow-[4px_8px_24px_rgba(27,27,24,0.06)]">
              {doc.featured_image && <img src={doc.featured_image} alt="" className="w-full h-auto mb-6 border border-ink/10" />}
              <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-4">Case Study</p>
              <h1 className="font-serif text-3xl md:text-4xl leading-tight text-ink">{doc.title.en || "Untitled"}</h1>
              {doc.collaboration_partner && <p className="mt-2 font-hand text-terracotta text-lg">In collaboration with {doc.collaboration_partner}</p>}
              {doc.challenge.en && <><h2 className="font-serif text-xl mt-6 text-ink">Challenge</h2><p className="font-body text-ink-soft leading-relaxed">{doc.challenge.en}</p></>}
              {doc.approach.en && <><h2 className="font-serif text-xl mt-6 text-ink">Approach</h2><p className="font-body text-ink-soft leading-relaxed">{doc.approach.en}</p></>}
              {doc.outcomes.en && <><h2 className="font-serif text-xl mt-6 text-ink">Outcomes</h2><div className="prose prose-notebook max-w-none font-body text-ink leading-relaxed"><ReactMarkdown>{doc.outcomes.en}</ReactMarkdown></div></>}
              {doc.metrics.en && <p className="mt-6 font-hand text-terracotta text-lg">{doc.metrics.en}</p>}
              {doc.testimonial.en && (
                <blockquote className="mt-6 border-l-2 border-terracotta pl-4 font-serif italic text-lg text-ink leading-snug">
                  &ldquo;{doc.testimonial.en}&rdquo;
                  {doc.testimonial_author && <p className="mt-2 font-hand text-terracotta text-base not-italic">— {doc.testimonial_author}</p>}
                </blockquote>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------
// List panels
// -----------------------------------------------------------------------------
const ItemList = ({ items, onEdit, testIdPrefix }) => {
  if (!items?.length) {
    return <p className="font-body text-ink-soft italic">No items yet. Create your first one.</p>;
  }
  return (
    <ul className="divide-y divide-ink/10 border border-ink/12 bg-paper" data-testid={`${testIdPrefix}-list`}>
      {items.map((it) => (
        <li key={it.id} className="flex items-center gap-4 px-5 py-4">
          <div className="flex-1 min-w-0">
            <p className="font-serif text-lg text-ink truncate">{it.title?.en || "(untitled)"}</p>
            <p className="font-body text-xs text-ink-soft mt-0.5 truncate">/{it.slug}</p>
          </div>
          <span className={`font-body text-xs uppercase tracking-[0.14em] px-2.5 py-1 border ${it.status === "published" ? "bg-mustard/20 border-mustard text-ink" : "bg-paper border-ink/20 text-ink-soft"}`}>
            {it.status}
          </span>
          <button
            type="button"
            onClick={() => onEdit(it.id)}
            className="font-body text-xs uppercase tracking-[0.14em] text-ink hover:text-terracotta transition-colors"
            data-testid={`${testIdPrefix}-edit-${it.slug}`}
          >
            Edit →
          </button>
        </li>
      ))}
    </ul>
  );
};

// -----------------------------------------------------------------------------
// Admin page
// -----------------------------------------------------------------------------
export default function InsightsAdmin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("articles"); // articles | cases
  const [articles, setArticles] = useState([]);
  const [cases, setCases] = useState([]);
  const [editing, setEditing] = useState(null); // { kind, doc }
  const [loginErr, setLoginErr] = useState("");
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");

  useEffect(() => {
    (async () => {
      if (getToken()) {
        try {
          const u = await api.me();
          setUser(u);
        } catch {
          setToken(null);
        }
      }
      setChecking(false);
    })();
  }, []);

  const refresh = async () => {
    const [a, c] = await Promise.all([api.listArticles(true), api.listCases(true)]);
    setArticles(a.items || []);
    setCases(c.items || []);
  };

  useEffect(() => {
    if (user) refresh().catch(() => {});
  }, [user]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginErr("");
    try {
      const r = await api.login(email.trim(), pw);
      setToken(r.access_token);
      setUser(r.user);
    } catch (err) {
      setLoginErr(err.message || "Login failed");
    }
  };

  const openNew = (kind) => setEditing({ kind, doc: null });
  const openEdit = async (kind, id) => {
    const fn = kind === "articles" ? api.getArticleAdmin : api.getCaseAdmin;
    const doc = await fn(id);
    setEditing({ kind, doc });
  };

  const handleSaveArticle = async (payload) => {
    const saved = payload.id ? await api.updateArticle(payload.id, payload) : await api.createArticle(payload);
    await refresh();
    setEditing({ kind: "articles", doc: saved });
    return saved;
  };
  const handleSaveCase = async (payload) => {
    const saved = payload.id ? await api.updateCase(payload.id, payload) : await api.createCase(payload);
    await refresh();
    setEditing({ kind: "cases", doc: saved });
    return saved;
  };
  const handleDelete = async (kind, id) => {
    if (!window.confirm("Delete this item permanently?")) return;
    if (kind === "articles") await api.deleteArticle(id); else await api.deleteCase(id);
    setEditing(null);
    await refresh();
  };

  const currentList = useMemo(() => (tab === "articles" ? articles : cases), [tab, articles, cases]);

  if (checking) {
    return <div className="min-h-screen flex items-center justify-center font-body text-ink-soft">Loading…</div>;
  }

  if (!user) {
    return (
      <section className="min-h-screen flex items-center justify-center px-5 py-24" data-testid="admin-login">
        <div className="w-full max-w-md bg-paper border border-ink/12 p-8 md:p-10 shadow-[4px_8px_28px_rgba(27,27,24,0.08)]">
          <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-3">Insights CMS</p>
          <h1 className="font-serif text-3xl leading-tight text-ink">Sign in to publish.</h1>
          <form onSubmit={handleLogin} className="mt-8 space-y-4" data-testid="login-form">
            <Field label="Email">
              <TextInput type="email" value={email} onChange={setEmail} required data-testid="login-email" />
            </Field>
            <Field label="Password">
              <TextInput type="password" value={pw} onChange={setPw} required data-testid="login-password" />
            </Field>
            {loginErr && <p className="font-body text-sm text-terracotta" data-testid="login-error">{loginErr}</p>}
            <button
              type="submit"
              className="w-full bg-ink text-paper py-3 font-body text-xs uppercase tracking-[0.14em] hover:bg-terracotta transition-colors"
              data-testid="login-submit"
            >
              Sign in
            </button>
          </form>
        </div>
      </section>
    );
  }

  return (
    <section className="min-h-screen py-16 md:py-24" data-testid="admin-panel">
      <div className="max-w-[1300px] mx-auto px-5 md:px-10">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-10">
          <div>
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta">Insights CMS</p>
            <h1 className="font-serif text-3xl md:text-4xl text-ink">Articles & Case Studies</h1>
          </div>
          <div className="flex items-center gap-4">
            <span className="font-body text-sm text-ink-soft">{user.email}</span>
            <button
              type="button"
              onClick={() => { setToken(null); setUser(null); }}
              className="font-body text-xs uppercase tracking-[0.14em] text-ink-soft hover:text-terracotta"
              data-testid="admin-logout"
            >
              Sign out
            </button>
          </div>
        </div>

        {editing ? (
          <>
            <button type="button" onClick={() => setEditing(null)}
              className="font-body text-xs uppercase tracking-[0.14em] text-ink-soft hover:text-terracotta mb-6" data-testid="back-to-list">
              ← Back to list
            </button>
            {editing.kind === "articles" ? (
              <ArticleEditor
                initial={editing.doc}
                onSave={handleSaveArticle}
                onCancel={() => setEditing(null)}
                onDelete={(id) => handleDelete("articles", id)}
                allArticles={articles}
              />
            ) : (
              <CaseEditor
                initial={editing.doc}
                onSave={handleSaveCase}
                onCancel={() => setEditing(null)}
                onDelete={(id) => handleDelete("cases", id)}
              />
            )}
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-6" data-testid="admin-tabs">
              {[
                { key: "articles", label: `Articles (${articles.length})` },
                { key: "cases",    label: `Case studies (${cases.length})` },
              ].map((t) => (
                <button key={t.key} type="button" onClick={() => setTab(t.key)}
                  data-testid={`tab-${t.key}`}
                  className={`px-4 py-2 font-body text-sm uppercase tracking-[0.14em] border ${tab === t.key ? "bg-ink text-paper border-ink" : "border-ink/20 text-ink-soft hover:border-terracotta hover:text-terracotta"} transition-colors`}>
                  {t.label}
                </button>
              ))}
              <button type="button" onClick={() => openNew(tab)}
                data-testid="new-item"
                className="ml-auto bg-mustard text-ink px-5 py-2 font-body text-xs uppercase tracking-[0.14em] hover:bg-terracotta hover:text-paper transition-colors">
                + New {tab === "articles" ? "article" : "case study"}
              </button>
            </div>
            <ItemList
              items={currentList}
              onEdit={(id) => openEdit(tab, id)}
              testIdPrefix={tab === "articles" ? "articles" : "cases"}
            />
          </>
        )}
      </div>
    </section>
  );
}
